import { FC, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';

import {
    Alert,
    Button,
    Form,
    SpaceBetween
} from '@cloudscape-design/components';

import LoadingSpinner from '../../LoadingSpinner';
import GcpProjectSubnetDetails from '../SubnetDetails';

import { useApprovalFormStore } from '../../../stores/gcpProjects/approvalFormStore';
import useSingleGcpProject from '../../../hooks/query/useSingleGcpProject';
import useAcceptGcpProject from '../../../hooks/mutate/useAcceptGcpProject';
import useUpdateGcpProjectDetails from '../../../hooks/mutate/useUpdateGcpProjectDetails';
import type { gcpProjectApprovalRequest } from '../../../types/gcpProject';
import useApproveTags from '../../../hooks/mutate/useApproveTags';
import { ApproveTags } from '../../../types/tags';
import {
    buildSubnetDetailsPayload,
    isGcpProjectGke,
    validateSubnetDetailsForApproval
} from '../../../helper/gcpProjectSubnetValidation';

interface ApprovalFormProps {
    thisgcpProjectID: string;
    shouldShowApprovalForm: (shouldShow: boolean) => void;
}

const ApprovalForm: FC<ApprovalFormProps> = ({
    thisgcpProjectID,
    shouldShowApprovalForm
}) => {
    const { authState } = useOktaAuth();
    const thisUserEmail = authState?.idToken?.claims.email as string;
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubnetValid, setIsSubnetValid] = useState(false);

    const { formValues, actions: { clearFormValues } } = useApprovalFormStore();

    const { data: thisgcpProject, isLoading: singlegcpProjectDataLoading } =
        useSingleGcpProject(thisgcpProjectID);
    const { mutateAsync: approveTagsById } = useApproveTags(thisgcpProject?.tagsID || '');
    const { mutateAsync: acceptgcpProject } = useAcceptGcpProject(thisgcpProjectID);
    const { mutateAsync: updateGcpProjectDetails } =
        useUpdateGcpProjectDetails(thisgcpProjectID);

    if (!thisgcpProject || singlegcpProjectDataLoading) return <LoadingSpinner />;

    const isSandboxProject = String(thisgcpProject.type || '').toLowerCase() === 'sandbox';
    const infrastructureType = String(thisgcpProject.details?.infrastructureType || '');
    const isGkeProject = !isSandboxProject && isGcpProjectGke(thisgcpProject.details || {});
    const isCommonNetworkLoading =
        !isSandboxProject && formValues.commonNetworkLoading === true;
    // Staging/Production can't be approved before their Develop request.
    const isDevelopRequestMissing =
        !isSandboxProject && formValues.developRequestMissing === true;

    const _handleApprove = async () => {
        const subnetInput = {
            infrastructureType,
            nonGkeAddSubnets: formValues.nonGkeAddSubnets,
            primarySubnet: formValues.primarySubnet,
            controlPlaneSubnet: formValues.controlPlaneSubnet,
            secondaryPodsRange: formValues.secondaryPodsRange,
            secondaryServicesRange: formValues.secondaryServicesRange,
            primarySubnetResourceType: formValues.primarySubnetResourceType,
            primarySubnetPurpose: formValues.primarySubnetPurpose,
            additionalSubnets: formValues.additionalSubnets,
            commonNetworkRequired: formValues.commonNetworkRequired,
            commonNetworkResources: formValues.commonNetworkResources,
            commonNetworkInherited: formValues.commonNetworkInherited === true,
            developRequestMissing: isDevelopRequestMissing
        };
        // Wait for the Develop lookup so an empty copy is never saved by accident.
        if (isCommonNetworkLoading) {
            return;
        }

        if (!isSandboxProject && !validateSubnetDetailsForApproval(subnetInput)) {
            setSubmitError(
                isGkeProject
                    ? 'Please complete all required GKE subnet or common network details before approving.'
                    : 'Please fix invalid subnet or common network details before approving.'
            );
            return;
        }

        const subnetDetails = isSandboxProject
            ? {}
            : buildSubnetDetailsPayload(subnetInput);
        const mergedDetails = {
            ...(thisgcpProject.details || {}),
            ...subnetDetails
        };

        const tagRequest: ApproveTags = {
            action: 'UPDATE_APPROVE_STATUS',
            status: 'APPROVED',
            tagsID: thisgcpProject?.tagsID || ''
        };

        const approvalRequest: gcpProjectApprovalRequest = {
            gcpProjectID: thisgcpProjectID,
            action: 'APPROVE',
            approverID: thisUserEmail,
            details: mergedDetails
        };

        try {
            setIsSubmitting(true);
            setSubmitError('');

            if (!isSandboxProject) {
                await updateGcpProjectDetails(subnetDetails);
            }
            await approveTagsById(tagRequest);
            await acceptgcpProject(approvalRequest);

            shouldShowApprovalForm(false);
            clearFormValues();
        } catch (error) {
            setSubmitError(
                error instanceof Error
                    ? error.message
                    : 'Unable to approve this GCP project right now.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form
            actions={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => shouldShowApprovalForm(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        loading={isSubmitting}
                        disabled={
                            isCommonNetworkLoading ||
                            isDevelopRequestMissing ||
                            (!isSandboxProject &&
                            !isSubnetValid &&
                            (isGkeProject ||
                                formValues.nonGkeAddSubnets === 'yes' ||
                                formValues.commonNetworkRequired === 'yes'))
                        }
                        onClick={_handleApprove}
                    >
                        Submit
                    </Button>
                </SpaceBetween>
            }
        >
            <SpaceBetween direction="vertical" size="l">
                {!isSandboxProject && (
                    <GcpProjectSubnetDetails
                        gcpProject={thisgcpProject}
                        embeddedInApproval
                        onSubnetValidityChange={setIsSubnetValid}
                    />
                )}
                {submitError && (
                    <Alert type="error" statusIconAriaLabel="Error">
                        {submitError}
                    </Alert>
                )}
            </SpaceBetween>
        </Form>
    );
};

export default ApprovalForm;
